// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// Code generated by Microsoft (R) AutoRest Code Generator.
// Changes may cause incorrect behavior and will be lost if the code is regenerated.

import type { PagedAsyncIterableIterator, PageSettings } from "@azure/core-paging";
import { setContinuationToken } from "../pagingHelper.js";
import type { PublicIPAddresses } from "../operationsInterfaces/index.js";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers.js";
import * as Parameters from "../models/parameters.js";
import type { NetworkManagementClient } from "../networkManagementClient.js";
import type { SimplePollerLike, OperationState } from "@azure/core-lro";
import { createHttpPoller } from "@azure/core-lro";
import { createLroSpec } from "../lroImpl.js";
import type {
  PublicIPAddress,
  PublicIPAddressesListCloudServicePublicIPAddressesNextOptionalParams,
  PublicIPAddressesListCloudServicePublicIPAddressesOptionalParams,
  PublicIPAddressesListCloudServicePublicIPAddressesResponse,
  PublicIPAddressesListCloudServiceRoleInstancePublicIPAddressesNextOptionalParams,
  PublicIPAddressesListCloudServiceRoleInstancePublicIPAddressesOptionalParams,
  PublicIPAddressesListCloudServiceRoleInstancePublicIPAddressesResponse,
  PublicIPAddressesListAllNextOptionalParams,
  PublicIPAddressesListAllOptionalParams,
  PublicIPAddressesListAllResponse,
  PublicIPAddressesListNextOptionalParams,
  PublicIPAddressesListOptionalParams,
  PublicIPAddressesListResponse,
  PublicIPAddressesListVirtualMachineScaleSetPublicIPAddressesNextOptionalParams,
  PublicIPAddressesListVirtualMachineScaleSetPublicIPAddressesOptionalParams,
  PublicIPAddressesListVirtualMachineScaleSetPublicIPAddressesResponse,
  PublicIPAddressesListVirtualMachineScaleSetVMPublicIPAddressesNextOptionalParams,
  PublicIPAddressesListVirtualMachineScaleSetVMPublicIPAddressesOptionalParams,
  PublicIPAddressesListVirtualMachineScaleSetVMPublicIPAddressesResponse,
  PublicIPAddressesGetCloudServicePublicIPAddressOptionalParams,
  PublicIPAddressesGetCloudServicePublicIPAddressResponse,
  PublicIPAddressesDeleteOptionalParams,
  PublicIPAddressesGetOptionalParams,
  PublicIPAddressesGetResponse,
  PublicIPAddressesCreateOrUpdateOptionalParams,
  PublicIPAddressesCreateOrUpdateResponse,
  TagsObject,
  PublicIPAddressesUpdateTagsOptionalParams,
  PublicIPAddressesUpdateTagsResponse,
  PublicIPAddressesDdosProtectionStatusOptionalParams,
  PublicIPAddressesDdosProtectionStatusResponse,
  ReserveCloudServicePublicIpAddressRequest,
  PublicIPAddressesReserveCloudServicePublicIpAddressOptionalParams,
  PublicIPAddressesReserveCloudServicePublicIpAddressResponse,
  DisassociateCloudServicePublicIpRequest,
  PublicIPAddressesDisassociateCloudServiceReservedPublicIpOptionalParams,
  PublicIPAddressesDisassociateCloudServiceReservedPublicIpResponse,
  PublicIPAddressesGetVirtualMachineScaleSetPublicIPAddressOptionalParams,
  PublicIPAddressesGetVirtualMachineScaleSetPublicIPAddressResponse,
  PublicIPAddressesListCloudServicePublicIPAddressesNextResponse,
  PublicIPAddressesListCloudServiceRoleInstancePublicIPAddressesNextResponse,
  PublicIPAddressesListAllNextResponse,
  PublicIPAddressesListNextResponse,
  PublicIPAddressesListVirtualMachineScaleSetPublicIPAddressesNextResponse,
  PublicIPAddressesListVirtualMachineScaleSetVMPublicIPAddressesNextResponse,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Class containing PublicIPAddresses operations. */
export class PublicIPAddressesImpl implements PublicIPAddresses {
  private readonly client: NetworkManagementClient;

  /**
   * Initialize a new instance of the class PublicIPAddresses class.
   * @param client Reference to the service client
   */
  constructor(client: NetworkManagementClient) {
    this.client = client;
  }

  /**
   * Gets information about all public IP addresses on a cloud service level.
   * @param resourceGroupName The name of the resource group.
   * @param cloudServiceName The name of the cloud service.
   * @param options The options parameters.
   */
  public listCloudServicePublicIPAddresses(
    resourceGroupName: string,
    cloudServiceName: string,
    options?: PublicIPAddressesListCloudServicePublicIPAddressesOptionalParams,
  ): PagedAsyncIterableIterator<PublicIPAddress> {
    const iter = this.listCloudServicePublicIPAddressesPagingAll(
      resourceGroupName,
      cloudServiceName,
      options,
    );
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listCloudServicePublicIPAddressesPagingPage(
          resourceGroupName,
          cloudServiceName,
          options,
          settings,
        );
      },
    };
  }

  private async *listCloudServicePublicIPAddressesPagingPage(
    resourceGroupName: string,
    cloudServiceName: string,
    options?: PublicIPAddressesListCloudServicePublicIPAddressesOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<PublicIPAddress[]> {
    let result: PublicIPAddressesListCloudServicePublicIPAddressesResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listCloudServicePublicIPAddresses(
        resourceGroupName,
        cloudServiceName,
        options,
      );
      const page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listCloudServicePublicIPAddressesNext(
        resourceGroupName,
        cloudServiceName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      const page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listCloudServicePublicIPAddressesPagingAll(
    resourceGroupName: string,
    cloudServiceName: string,
    options?: PublicIPAddressesListCloudServicePublicIPAddressesOptionalParams,
  ): AsyncIterableIterator<PublicIPAddress> {
    for await (const page of this.listCloudServicePublicIPAddressesPagingPage(
      resourceGroupName,
      cloudServiceName,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Gets information about all public IP addresses in a role instance IP configuration in a cloud
   * service.
   * @param resourceGroupName The name of the resource group.
   * @param cloudServiceName The name of the cloud service.
   * @param roleInstanceName The name of role instance.
   * @param networkInterfaceName The network interface name.
   * @param ipConfigurationName The IP configuration name.
   * @param options The options parameters.
   */
  public listCloudServiceRoleInstancePublicIPAddresses(
    resourceGroupName: string,
    cloudServiceName: string,
    roleInstanceName: string,
    networkInterfaceName: string,
    ipConfigurationName: string,
    options?: PublicIPAddressesListCloudServiceRoleInstancePublicIPAddressesOptionalParams,
  ): PagedAsyncIterableIterator<PublicIPAddress> {
    const iter = this.listCloudServiceRoleInstancePublicIPAddressesPagingAll(
      resourceGroupName,
      cloudServiceName,
      roleInstanceName,
      networkInterfaceName,
      ipConfigurationName,
      options,
    );
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listCloudServiceRoleInstancePublicIPAddressesPagingPage(
          resourceGroupName,
          cloudServiceName,
          roleInstanceName,
          networkInterfaceName,
          ipConfigurationName,
          options,
          settings,
        );
      },
    };
  }

  private async *listCloudServiceRoleInstancePublicIPAddressesPagingPage(
    resourceGroupName: string,
    cloudServiceName: string,
    roleInstanceName: string,
    networkInterfaceName: string,
    ipConfigurationName: string,
    options?: PublicIPAddressesListCloudServiceRoleInstancePublicIPAddressesOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<PublicIPAddress[]> {
    let result: PublicIPAddressesListCloudServiceRoleInstancePublicIPAddressesResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listCloudServiceRoleInstancePublicIPAddresses(
        resourceGroupName,
        cloudServiceName,
        roleInstanceName,
        networkInterfaceName,
        ipConfigurationName,
        options,
      );
      const page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listCloudServiceRoleInstancePublicIPAddressesNext(
        resourceGroupName,
        cloudServiceName,
        roleInstanceName,
        networkInterfaceName,
        ipConfigurationName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      const page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listCloudServiceRoleInstancePublicIPAddressesPagingAll(
    resourceGroupName: string,
    cloudServiceName: string,
    roleInstanceName: string,
    networkInterfaceName: string,
    ipConfigurationName: string,
    options?: PublicIPAddressesListCloudServiceRoleInstancePublicIPAddressesOptionalParams,
  ): AsyncIterableIterator<PublicIPAddress> {
    for await (const page of this.listCloudServiceRoleInstancePublicIPAddressesPagingPage(
      resourceGroupName,
      cloudServiceName,
      roleInstanceName,
      networkInterfaceName,
      ipConfigurationName,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Gets all the public IP addresses in a subscription.
   * @param options The options parameters.
   */
  public listAll(
    options?: PublicIPAddressesListAllOptionalParams,
  ): PagedAsyncIterableIterator<PublicIPAddress> {
    const iter = this.listAllPagingAll(options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listAllPagingPage(options, settings);
      },
    };
  }

  private async *listAllPagingPage(
    options?: PublicIPAddressesListAllOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<PublicIPAddress[]> {
    let result: PublicIPAddressesListAllResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listAll(options);
      const page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listAllNext(continuationToken, options);
      continuationToken = result.nextLink;
      const page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listAllPagingAll(
    options?: PublicIPAddressesListAllOptionalParams,
  ): AsyncIterableIterator<PublicIPAddress> {
    for await (const page of this.listAllPagingPage(options)) {
      yield* page;
    }
  }

  /**
   * Gets all public IP addresses in a resource group.
   * @param resourceGroupName The name of the resource group.
   * @param options The options parameters.
   */
  public list(
    resourceGroupName: string,
    options?: PublicIPAddressesListOptionalParams,
  ): PagedAsyncIterableIterator<PublicIPAddress> {
    const iter = this.listPagingAll(resourceGroupName, options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listPagingPage(resourceGroupName, options, settings);
      },
    };
  }

  private async *listPagingPage(
    resourceGroupName: string,
    options?: PublicIPAddressesListOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<PublicIPAddress[]> {
    let result: PublicIPAddressesListResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._list(resourceGroupName, options);
      const page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listNext(resourceGroupName, continuationToken, options);
      continuationToken = result.nextLink;
      const page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listPagingAll(
    resourceGroupName: string,
    options?: PublicIPAddressesListOptionalParams,
  ): AsyncIterableIterator<PublicIPAddress> {
    for await (const page of this.listPagingPage(resourceGroupName, options)) {
      yield* page;
    }
  }

  /**
   * Gets information about all public IP addresses on a virtual machine scale set level.
   * @param resourceGroupName The name of the resource group.
   * @param virtualMachineScaleSetName The name of the virtual machine scale set.
   * @param options The options parameters.
   */
  public listVirtualMachineScaleSetPublicIPAddresses(
    resourceGroupName: string,
    virtualMachineScaleSetName: string,
    options?: PublicIPAddressesListVirtualMachineScaleSetPublicIPAddressesOptionalParams,
  ): PagedAsyncIterableIterator<PublicIPAddress> {
    const iter = this.listVirtualMachineScaleSetPublicIPAddressesPagingAll(
      resourceGroupName,
      virtualMachineScaleSetName,
      options,
    );
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listVirtualMachineScaleSetPublicIPAddressesPagingPage(
          resourceGroupName,
          virtualMachineScaleSetName,
          options,
          settings,
        );
      },
    };
  }

  private async *listVirtualMachineScaleSetPublicIPAddressesPagingPage(
    resourceGroupName: string,
    virtualMachineScaleSetName: string,
    options?: PublicIPAddressesListVirtualMachineScaleSetPublicIPAddressesOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<PublicIPAddress[]> {
    let result: PublicIPAddressesListVirtualMachineScaleSetPublicIPAddressesResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listVirtualMachineScaleSetPublicIPAddresses(
        resourceGroupName,
        virtualMachineScaleSetName,
        options,
      );
      const page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listVirtualMachineScaleSetPublicIPAddressesNext(
        resourceGroupName,
        virtualMachineScaleSetName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      const page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listVirtualMachineScaleSetPublicIPAddressesPagingAll(
    resourceGroupName: string,
    virtualMachineScaleSetName: string,
    options?: PublicIPAddressesListVirtualMachineScaleSetPublicIPAddressesOptionalParams,
  ): AsyncIterableIterator<PublicIPAddress> {
    for await (const page of this.listVirtualMachineScaleSetPublicIPAddressesPagingPage(
      resourceGroupName,
      virtualMachineScaleSetName,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Gets information about all public IP addresses in a virtual machine IP configuration in a virtual
   * machine scale set.
   * @param resourceGroupName The name of the resource group.
   * @param virtualMachineScaleSetName The name of the virtual machine scale set.
   * @param virtualmachineIndex The virtual machine index.
   * @param networkInterfaceName The network interface name.
   * @param ipConfigurationName The IP configuration name.
   * @param options The options parameters.
   */
  public listVirtualMachineScaleSetVMPublicIPAddresses(
    resourceGroupName: string,
    virtualMachineScaleSetName: string,
    virtualmachineIndex: string,
    networkInterfaceName: string,
    ipConfigurationName: string,
    options?: PublicIPAddressesListVirtualMachineScaleSetVMPublicIPAddressesOptionalParams,
  ): PagedAsyncIterableIterator<PublicIPAddress> {
    const iter = this.listVirtualMachineScaleSetVMPublicIPAddressesPagingAll(
      resourceGroupName,
      virtualMachineScaleSetName,
      virtualmachineIndex,
      networkInterfaceName,
      ipConfigurationName,
      options,
    );
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listVirtualMachineScaleSetVMPublicIPAddressesPagingPage(
          resourceGroupName,
          virtualMachineScaleSetName,
          virtualmachineIndex,
          networkInterfaceName,
          ipConfigurationName,
          options,
          settings,
        );
      },
    };
  }

  private async *listVirtualMachineScaleSetVMPublicIPAddressesPagingPage(
    resourceGroupName: string,
    virtualMachineScaleSetName: string,
    virtualmachineIndex: string,
    networkInterfaceName: string,
    ipConfigurationName: string,
    options?: PublicIPAddressesListVirtualMachineScaleSetVMPublicIPAddressesOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<PublicIPAddress[]> {
    let result: PublicIPAddressesListVirtualMachineScaleSetVMPublicIPAddressesResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listVirtualMachineScaleSetVMPublicIPAddresses(
        resourceGroupName,
        virtualMachineScaleSetName,
        virtualmachineIndex,
        networkInterfaceName,
        ipConfigurationName,
        options,
      );
      const page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listVirtualMachineScaleSetVMPublicIPAddressesNext(
        resourceGroupName,
        virtualMachineScaleSetName,
        virtualmachineIndex,
        networkInterfaceName,
        ipConfigurationName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      const page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listVirtualMachineScaleSetVMPublicIPAddressesPagingAll(
    resourceGroupName: string,
    virtualMachineScaleSetName: string,
    virtualmachineIndex: string,
    networkInterfaceName: string,
    ipConfigurationName: string,
    options?: PublicIPAddressesListVirtualMachineScaleSetVMPublicIPAddressesOptionalParams,
  ): AsyncIterableIterator<PublicIPAddress> {
    for await (const page of this.listVirtualMachineScaleSetVMPublicIPAddressesPagingPage(
      resourceGroupName,
      virtualMachineScaleSetName,
      virtualmachineIndex,
      networkInterfaceName,
      ipConfigurationName,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Gets information about all public IP addresses on a cloud service level.
   * @param resourceGroupName The name of the resource group.
   * @param cloudServiceName The name of the cloud service.
   * @param options The options parameters.
   */
  private _listCloudServicePublicIPAddresses(
    resourceGroupName: string,
    cloudServiceName: string,
    options?: PublicIPAddressesListCloudServicePublicIPAddressesOptionalParams,
  ): Promise<PublicIPAddressesListCloudServicePublicIPAddressesResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, cloudServiceName, options },
      listCloudServicePublicIPAddressesOperationSpec,
    );
  }

  /**
   * Gets information about all public IP addresses in a role instance IP configuration in a cloud
   * service.
   * @param resourceGroupName The name of the resource group.
   * @param cloudServiceName The name of the cloud service.
   * @param roleInstanceName The name of role instance.
   * @param networkInterfaceName The network interface name.
   * @param ipConfigurationName The IP configuration name.
   * @param options The options parameters.
   */
  private _listCloudServiceRoleInstancePublicIPAddresses(
    resourceGroupName: string,
    cloudServiceName: string,
    roleInstanceName: string,
    networkInterfaceName: string,
    ipConfigurationName: string,
    options?: PublicIPAddressesListCloudServiceRoleInstancePublicIPAddressesOptionalParams,
  ): Promise<PublicIPAddressesListCloudServiceRoleInstancePublicIPAddressesResponse> {
    return this.client.sendOperationRequest(
      {
        resourceGroupName,
        cloudServiceName,
        roleInstanceName,
        networkInterfaceName,
        ipConfigurationName,
        options,
      },
      listCloudServiceRoleInstancePublicIPAddressesOperationSpec,
    );
  }

  /**
   * Get the specified public IP address in a cloud service.
   * @param resourceGroupName The name of the resource group.
   * @param cloudServiceName The name of the cloud service.
   * @param roleInstanceName The role instance name.
   * @param networkInterfaceName The name of the network interface.
   * @param ipConfigurationName The name of the IP configuration.
   * @param publicIpAddressName The name of the public IP Address.
   * @param options The options parameters.
   */
  getCloudServicePublicIPAddress(
    resourceGroupName: string,
    cloudServiceName: string,
    roleInstanceName: string,
    networkInterfaceName: string,
    ipConfigurationName: string,
    publicIpAddressName: string,
    options?: PublicIPAddressesGetCloudServicePublicIPAddressOptionalParams,
  ): Promise<PublicIPAddressesGetCloudServicePublicIPAddressResponse> {
    return this.client.sendOperationRequest(
      {
        resourceGroupName,
        cloudServiceName,
        roleInstanceName,
        networkInterfaceName,
        ipConfigurationName,
        publicIpAddressName,
        options,
      },
      getCloudServicePublicIPAddressOperationSpec,
    );
  }

  /**
   * Deletes the specified public IP address.
   * @param resourceGroupName The name of the resource group.
   * @param publicIpAddressName The name of the public IP address.
   * @param options The options parameters.
   */
  async beginDelete(
    resourceGroupName: string,
    publicIpAddressName: string,
    options?: PublicIPAddressesDeleteOptionalParams,
  ): Promise<SimplePollerLike<OperationState<void>, void>> {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<void> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: { resourceGroupName, publicIpAddressName, options },
      spec: deleteOperationSpec,
    });
    const poller = await createHttpPoller<void, OperationState<void>>(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Deletes the specified public IP address.
   * @param resourceGroupName The name of the resource group.
   * @param publicIpAddressName The name of the public IP address.
   * @param options The options parameters.
   */
  async beginDeleteAndWait(
    resourceGroupName: string,
    publicIpAddressName: string,
    options?: PublicIPAddressesDeleteOptionalParams,
  ): Promise<void> {
    const poller = await this.beginDelete(resourceGroupName, publicIpAddressName, options);
    return poller.pollUntilDone();
  }

  /**
   * Gets the specified public IP address in a specified resource group.
   * @param resourceGroupName The name of the resource group.
   * @param publicIpAddressName The name of the public IP address.
   * @param options The options parameters.
   */
  get(
    resourceGroupName: string,
    publicIpAddressName: string,
    options?: PublicIPAddressesGetOptionalParams,
  ): Promise<PublicIPAddressesGetResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, publicIpAddressName, options },
      getOperationSpec,
    );
  }

  /**
   * Creates or updates a static or dynamic public IP address.
   * @param resourceGroupName The name of the resource group.
   * @param publicIpAddressName The name of the public IP address.
   * @param parameters Parameters supplied to the create or update public IP address operation.
   * @param options The options parameters.
   */
  async beginCreateOrUpdate(
    resourceGroupName: string,
    publicIpAddressName: string,
    parameters: PublicIPAddress,
    options?: PublicIPAddressesCreateOrUpdateOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<PublicIPAddressesCreateOrUpdateResponse>,
      PublicIPAddressesCreateOrUpdateResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<PublicIPAddressesCreateOrUpdateResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: { resourceGroupName, publicIpAddressName, parameters, options },
      spec: createOrUpdateOperationSpec,
    });
    const poller = await createHttpPoller<
      PublicIPAddressesCreateOrUpdateResponse,
      OperationState<PublicIPAddressesCreateOrUpdateResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "azure-async-operation",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Creates or updates a static or dynamic public IP address.
   * @param resourceGroupName The name of the resource group.
   * @param publicIpAddressName The name of the public IP address.
   * @param parameters Parameters supplied to the create or update public IP address operation.
   * @param options The options parameters.
   */
  async beginCreateOrUpdateAndWait(
    resourceGroupName: string,
    publicIpAddressName: string,
    parameters: PublicIPAddress,
    options?: PublicIPAddressesCreateOrUpdateOptionalParams,
  ): Promise<PublicIPAddressesCreateOrUpdateResponse> {
    const poller = await this.beginCreateOrUpdate(
      resourceGroupName,
      publicIpAddressName,
      parameters,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * Updates public IP address tags.
   * @param resourceGroupName The name of the resource group.
   * @param publicIpAddressName The name of the public IP address.
   * @param parameters Parameters supplied to update public IP address tags.
   * @param options The options parameters.
   */
  updateTags(
    resourceGroupName: string,
    publicIpAddressName: string,
    parameters: TagsObject,
    options?: PublicIPAddressesUpdateTagsOptionalParams,
  ): Promise<PublicIPAddressesUpdateTagsResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, publicIpAddressName, parameters, options },
      updateTagsOperationSpec,
    );
  }

  /**
   * Gets all the public IP addresses in a subscription.
   * @param options The options parameters.
   */
  private _listAll(
    options?: PublicIPAddressesListAllOptionalParams,
  ): Promise<PublicIPAddressesListAllResponse> {
    return this.client.sendOperationRequest({ options }, listAllOperationSpec);
  }

  /**
   * Gets all public IP addresses in a resource group.
   * @param resourceGroupName The name of the resource group.
   * @param options The options parameters.
   */
  private _list(
    resourceGroupName: string,
    options?: PublicIPAddressesListOptionalParams,
  ): Promise<PublicIPAddressesListResponse> {
    return this.client.sendOperationRequest({ resourceGroupName, options }, listOperationSpec);
  }

  /**
   * Gets the Ddos Protection Status of a Public IP Address
   * @param resourceGroupName The name of the resource group.
   * @param publicIpAddressName The name of the public IP address.
   * @param options The options parameters.
   */
  async beginDdosProtectionStatus(
    resourceGroupName: string,
    publicIpAddressName: string,
    options?: PublicIPAddressesDdosProtectionStatusOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<PublicIPAddressesDdosProtectionStatusResponse>,
      PublicIPAddressesDdosProtectionStatusResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<PublicIPAddressesDdosProtectionStatusResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: { resourceGroupName, publicIpAddressName, options },
      spec: ddosProtectionStatusOperationSpec,
    });
    const poller = await createHttpPoller<
      PublicIPAddressesDdosProtectionStatusResponse,
      OperationState<PublicIPAddressesDdosProtectionStatusResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Gets the Ddos Protection Status of a Public IP Address
   * @param resourceGroupName The name of the resource group.
   * @param publicIpAddressName The name of the public IP address.
   * @param options The options parameters.
   */
  async beginDdosProtectionStatusAndWait(
    resourceGroupName: string,
    publicIpAddressName: string,
    options?: PublicIPAddressesDdosProtectionStatusOptionalParams,
  ): Promise<PublicIPAddressesDdosProtectionStatusResponse> {
    const poller = await this.beginDdosProtectionStatus(
      resourceGroupName,
      publicIpAddressName,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * Reserves the specified Cloud Service Public IP by switching its allocation method to Static. If
   * rollback is requested, reverts the allocation method to Dynamic.
   * @param resourceGroupName The name of the resource group.
   * @param publicIpAddressName The name of the public IP address.
   * @param parameters Parameter that define which Public IP Address should be associated in place of
   *                   given Public IP Address.
   * @param options The options parameters.
   */
  async beginReserveCloudServicePublicIpAddress(
    resourceGroupName: string,
    publicIpAddressName: string,
    parameters: ReserveCloudServicePublicIpAddressRequest,
    options?: PublicIPAddressesReserveCloudServicePublicIpAddressOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<PublicIPAddressesReserveCloudServicePublicIpAddressResponse>,
      PublicIPAddressesReserveCloudServicePublicIpAddressResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<PublicIPAddressesReserveCloudServicePublicIpAddressResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: { resourceGroupName, publicIpAddressName, parameters, options },
      spec: reserveCloudServicePublicIpAddressOperationSpec,
    });
    const poller = await createHttpPoller<
      PublicIPAddressesReserveCloudServicePublicIpAddressResponse,
      OperationState<PublicIPAddressesReserveCloudServicePublicIpAddressResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Reserves the specified Cloud Service Public IP by switching its allocation method to Static. If
   * rollback is requested, reverts the allocation method to Dynamic.
   * @param resourceGroupName The name of the resource group.
   * @param publicIpAddressName The name of the public IP address.
   * @param parameters Parameter that define which Public IP Address should be associated in place of
   *                   given Public IP Address.
   * @param options The options parameters.
   */
  async beginReserveCloudServicePublicIpAddressAndWait(
    resourceGroupName: string,
    publicIpAddressName: string,
    parameters: ReserveCloudServicePublicIpAddressRequest,
    options?: PublicIPAddressesReserveCloudServicePublicIpAddressOptionalParams,
  ): Promise<PublicIPAddressesReserveCloudServicePublicIpAddressResponse> {
    const poller = await this.beginReserveCloudServicePublicIpAddress(
      resourceGroupName,
      publicIpAddressName,
      parameters,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * Disassociates the Cloud Service reserved Public IP and associates the specified Standalone Public IP
   * to the same Cloud Service frontend.
   * @param resourceGroupName The name of the resource group.
   * @param publicIpAddressName The name of the public IP address.
   * @param parameters Parameter that define which Public IP Address should be associated in place of
   *                   given Public IP Address.
   * @param options The options parameters.
   */
  async beginDisassociateCloudServiceReservedPublicIp(
    resourceGroupName: string,
    publicIpAddressName: string,
    parameters: DisassociateCloudServicePublicIpRequest,
    options?: PublicIPAddressesDisassociateCloudServiceReservedPublicIpOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<PublicIPAddressesDisassociateCloudServiceReservedPublicIpResponse>,
      PublicIPAddressesDisassociateCloudServiceReservedPublicIpResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<PublicIPAddressesDisassociateCloudServiceReservedPublicIpResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: { resourceGroupName, publicIpAddressName, parameters, options },
      spec: disassociateCloudServiceReservedPublicIpOperationSpec,
    });
    const poller = await createHttpPoller<
      PublicIPAddressesDisassociateCloudServiceReservedPublicIpResponse,
      OperationState<PublicIPAddressesDisassociateCloudServiceReservedPublicIpResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Disassociates the Cloud Service reserved Public IP and associates the specified Standalone Public IP
   * to the same Cloud Service frontend.
   * @param resourceGroupName The name of the resource group.
   * @param publicIpAddressName The name of the public IP address.
   * @param parameters Parameter that define which Public IP Address should be associated in place of
   *                   given Public IP Address.
   * @param options The options parameters.
   */
  async beginDisassociateCloudServiceReservedPublicIpAndWait(
    resourceGroupName: string,
    publicIpAddressName: string,
    parameters: DisassociateCloudServicePublicIpRequest,
    options?: PublicIPAddressesDisassociateCloudServiceReservedPublicIpOptionalParams,
  ): Promise<PublicIPAddressesDisassociateCloudServiceReservedPublicIpResponse> {
    const poller = await this.beginDisassociateCloudServiceReservedPublicIp(
      resourceGroupName,
      publicIpAddressName,
      parameters,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * Gets information about all public IP addresses on a virtual machine scale set level.
   * @param resourceGroupName The name of the resource group.
   * @param virtualMachineScaleSetName The name of the virtual machine scale set.
   * @param options The options parameters.
   */
  private _listVirtualMachineScaleSetPublicIPAddresses(
    resourceGroupName: string,
    virtualMachineScaleSetName: string,
    options?: PublicIPAddressesListVirtualMachineScaleSetPublicIPAddressesOptionalParams,
  ): Promise<PublicIPAddressesListVirtualMachineScaleSetPublicIPAddressesResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, virtualMachineScaleSetName, options },
      listVirtualMachineScaleSetPublicIPAddressesOperationSpec,
    );
  }

  /**
   * Gets information about all public IP addresses in a virtual machine IP configuration in a virtual
   * machine scale set.
   * @param resourceGroupName The name of the resource group.
   * @param virtualMachineScaleSetName The name of the virtual machine scale set.
   * @param virtualmachineIndex The virtual machine index.
   * @param networkInterfaceName The network interface name.
   * @param ipConfigurationName The IP configuration name.
   * @param options The options parameters.
   */
  private _listVirtualMachineScaleSetVMPublicIPAddresses(
    resourceGroupName: string,
    virtualMachineScaleSetName: string,
    virtualmachineIndex: string,
    networkInterfaceName: string,
    ipConfigurationName: string,
    options?: PublicIPAddressesListVirtualMachineScaleSetVMPublicIPAddressesOptionalParams,
  ): Promise<PublicIPAddressesListVirtualMachineScaleSetVMPublicIPAddressesResponse> {
    return this.client.sendOperationRequest(
      {
        resourceGroupName,
        virtualMachineScaleSetName,
        virtualmachineIndex,
        networkInterfaceName,
        ipConfigurationName,
        options,
      },
      listVirtualMachineScaleSetVMPublicIPAddressesOperationSpec,
    );
  }

  /**
   * Get the specified public IP address in a virtual machine scale set.
   * @param resourceGroupName The name of the resource group.
   * @param virtualMachineScaleSetName The name of the virtual machine scale set.
   * @param virtualmachineIndex The virtual machine index.
   * @param networkInterfaceName The name of the network interface.
   * @param ipConfigurationName The name of the IP configuration.
   * @param publicIpAddressName The name of the public IP Address.
   * @param options The options parameters.
   */
  getVirtualMachineScaleSetPublicIPAddress(
    resourceGroupName: string,
    virtualMachineScaleSetName: string,
    virtualmachineIndex: string,
    networkInterfaceName: string,
    ipConfigurationName: string,
    publicIpAddressName: string,
    options?: PublicIPAddressesGetVirtualMachineScaleSetPublicIPAddressOptionalParams,
  ): Promise<PublicIPAddressesGetVirtualMachineScaleSetPublicIPAddressResponse> {
    return this.client.sendOperationRequest(
      {
        resourceGroupName,
        virtualMachineScaleSetName,
        virtualmachineIndex,
        networkInterfaceName,
        ipConfigurationName,
        publicIpAddressName,
        options,
      },
      getVirtualMachineScaleSetPublicIPAddressOperationSpec,
    );
  }

  /**
   * ListCloudServicePublicIPAddressesNext
   * @param resourceGroupName The name of the resource group.
   * @param cloudServiceName The name of the cloud service.
   * @param nextLink The nextLink from the previous successful call to the
   *                 ListCloudServicePublicIPAddresses method.
   * @param options The options parameters.
   */
  private _listCloudServicePublicIPAddressesNext(
    resourceGroupName: string,
    cloudServiceName: string,
    nextLink: string,
    options?: PublicIPAddressesListCloudServicePublicIPAddressesNextOptionalParams,
  ): Promise<PublicIPAddressesListCloudServicePublicIPAddressesNextResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, cloudServiceName, nextLink, options },
      listCloudServicePublicIPAddressesNextOperationSpec,
    );
  }

  /**
   * ListCloudServiceRoleInstancePublicIPAddressesNext
   * @param resourceGroupName The name of the resource group.
   * @param cloudServiceName The name of the cloud service.
   * @param roleInstanceName The name of role instance.
   * @param networkInterfaceName The network interface name.
   * @param ipConfigurationName The IP configuration name.
   * @param nextLink The nextLink from the previous successful call to the
   *                 ListCloudServiceRoleInstancePublicIPAddresses method.
   * @param options The options parameters.
   */
  private _listCloudServiceRoleInstancePublicIPAddressesNext(
    resourceGroupName: string,
    cloudServiceName: string,
    roleInstanceName: string,
    networkInterfaceName: string,
    ipConfigurationName: string,
    nextLink: string,
    options?: PublicIPAddressesListCloudServiceRoleInstancePublicIPAddressesNextOptionalParams,
  ): Promise<PublicIPAddressesListCloudServiceRoleInstancePublicIPAddressesNextResponse> {
    return this.client.sendOperationRequest(
      {
        resourceGroupName,
        cloudServiceName,
        roleInstanceName,
        networkInterfaceName,
        ipConfigurationName,
        nextLink,
        options,
      },
      listCloudServiceRoleInstancePublicIPAddressesNextOperationSpec,
    );
  }

  /**
   * ListAllNext
   * @param nextLink The nextLink from the previous successful call to the ListAll method.
   * @param options The options parameters.
   */
  private _listAllNext(
    nextLink: string,
    options?: PublicIPAddressesListAllNextOptionalParams,
  ): Promise<PublicIPAddressesListAllNextResponse> {
    return this.client.sendOperationRequest({ nextLink, options }, listAllNextOperationSpec);
  }

  /**
   * ListNext
   * @param resourceGroupName The name of the resource group.
   * @param nextLink The nextLink from the previous successful call to the List method.
   * @param options The options parameters.
   */
  private _listNext(
    resourceGroupName: string,
    nextLink: string,
    options?: PublicIPAddressesListNextOptionalParams,
  ): Promise<PublicIPAddressesListNextResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, nextLink, options },
      listNextOperationSpec,
    );
  }

  /**
   * ListVirtualMachineScaleSetPublicIPAddressesNext
   * @param resourceGroupName The name of the resource group.
   * @param virtualMachineScaleSetName The name of the virtual machine scale set.
   * @param nextLink The nextLink from the previous successful call to the
   *                 ListVirtualMachineScaleSetPublicIPAddresses method.
   * @param options The options parameters.
   */
  private _listVirtualMachineScaleSetPublicIPAddressesNext(
    resourceGroupName: string,
    virtualMachineScaleSetName: string,
    nextLink: string,
    options?: PublicIPAddressesListVirtualMachineScaleSetPublicIPAddressesNextOptionalParams,
  ): Promise<PublicIPAddressesListVirtualMachineScaleSetPublicIPAddressesNextResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, virtualMachineScaleSetName, nextLink, options },
      listVirtualMachineScaleSetPublicIPAddressesNextOperationSpec,
    );
  }

  /**
   * ListVirtualMachineScaleSetVMPublicIPAddressesNext
   * @param resourceGroupName The name of the resource group.
   * @param virtualMachineScaleSetName The name of the virtual machine scale set.
   * @param virtualmachineIndex The virtual machine index.
   * @param networkInterfaceName The network interface name.
   * @param ipConfigurationName The IP configuration name.
   * @param nextLink The nextLink from the previous successful call to the
   *                 ListVirtualMachineScaleSetVMPublicIPAddresses method.
   * @param options The options parameters.
   */
  private _listVirtualMachineScaleSetVMPublicIPAddressesNext(
    resourceGroupName: string,
    virtualMachineScaleSetName: string,
    virtualmachineIndex: string,
    networkInterfaceName: string,
    ipConfigurationName: string,
    nextLink: string,
    options?: PublicIPAddressesListVirtualMachineScaleSetVMPublicIPAddressesNextOptionalParams,
  ): Promise<PublicIPAddressesListVirtualMachineScaleSetVMPublicIPAddressesNextResponse> {
    return this.client.sendOperationRequest(
      {
        resourceGroupName,
        virtualMachineScaleSetName,
        virtualmachineIndex,
        networkInterfaceName,
        ipConfigurationName,
        nextLink,
        options,
      },
      listVirtualMachineScaleSetVMPublicIPAddressesNextOperationSpec,
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const listCloudServicePublicIPAddressesOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Compute/cloudServices/{cloudServiceName}/publicipaddresses",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PublicIPAddressListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.cloudServiceName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listCloudServiceRoleInstancePublicIPAddressesOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Compute/cloudServices/{cloudServiceName}/roleInstances/{roleInstanceName}/networkInterfaces/{networkInterfaceName}/ipconfigurations/{ipConfigurationName}/publicipaddresses",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PublicIPAddressListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.cloudServiceName,
    Parameters.roleInstanceName,
    Parameters.networkInterfaceName,
    Parameters.ipConfigurationName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getCloudServicePublicIPAddressOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Compute/cloudServices/{cloudServiceName}/roleInstances/{roleInstanceName}/networkInterfaces/{networkInterfaceName}/ipconfigurations/{ipConfigurationName}/publicipaddresses/{publicIpAddressName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PublicIPAddress,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion, Parameters.expand],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.cloudServiceName,
    Parameters.roleInstanceName,
    Parameters.networkInterfaceName,
    Parameters.ipConfigurationName,
    Parameters.publicIpAddressName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const deleteOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/publicIPAddresses/{publicIpAddressName}",
  httpMethod: "DELETE",
  responses: {
    200: {},
    201: {},
    202: {},
    204: {},
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.publicIpAddressName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/publicIPAddresses/{publicIpAddressName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PublicIPAddress,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion, Parameters.expand],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.publicIpAddressName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const createOrUpdateOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/publicIPAddresses/{publicIpAddressName}",
  httpMethod: "PUT",
  responses: {
    200: {
      bodyMapper: Mappers.PublicIPAddress,
    },
    201: {
      bodyMapper: Mappers.PublicIPAddress,
    },
    202: {
      bodyMapper: Mappers.PublicIPAddress,
    },
    204: {
      bodyMapper: Mappers.PublicIPAddress,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  requestBody: Parameters.parameters10,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.publicIpAddressName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const updateTagsOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/publicIPAddresses/{publicIpAddressName}",
  httpMethod: "PATCH",
  responses: {
    200: {
      bodyMapper: Mappers.PublicIPAddress,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  requestBody: Parameters.parameters1,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.publicIpAddressName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const listAllOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/providers/Microsoft.Network/publicIPAddresses",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PublicIPAddressListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.$host, Parameters.subscriptionId],
  headerParameters: [Parameters.accept],
  serializer,
};
const listOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/publicIPAddresses",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PublicIPAddressListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.$host, Parameters.resourceGroupName, Parameters.subscriptionId],
  headerParameters: [Parameters.accept],
  serializer,
};
const ddosProtectionStatusOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/publicIPAddresses/{publicIpAddressName}/ddosProtectionStatus",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.PublicIpDdosProtectionStatusResult,
    },
    201: {
      bodyMapper: Mappers.PublicIpDdosProtectionStatusResult,
    },
    202: {
      bodyMapper: Mappers.PublicIpDdosProtectionStatusResult,
    },
    204: {
      bodyMapper: Mappers.PublicIpDdosProtectionStatusResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.publicIpAddressName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const reserveCloudServicePublicIpAddressOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/publicIPAddresses/{publicIpAddressName}/reserveCloudServicePublicIpAddress",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.PublicIPAddress,
    },
    201: {
      bodyMapper: Mappers.PublicIPAddress,
    },
    202: {
      bodyMapper: Mappers.PublicIPAddress,
    },
    204: {
      bodyMapper: Mappers.PublicIPAddress,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  requestBody: Parameters.parameters11,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.publicIpAddressName1,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const disassociateCloudServiceReservedPublicIpOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/publicIPAddresses/{publicIpAddressName}/disassociateCloudServiceReservedPublicIp",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.PublicIPAddress,
    },
    201: {
      bodyMapper: Mappers.PublicIPAddress,
    },
    202: {
      bodyMapper: Mappers.PublicIPAddress,
    },
    204: {
      bodyMapper: Mappers.PublicIPAddress,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  requestBody: Parameters.parameters12,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.publicIpAddressName1,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const listVirtualMachineScaleSetPublicIPAddressesOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Compute/virtualMachineScaleSets/{virtualMachineScaleSetName}/publicipaddresses",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PublicIPAddressListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion1],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.virtualMachineScaleSetName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listVirtualMachineScaleSetVMPublicIPAddressesOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Compute/virtualMachineScaleSets/{virtualMachineScaleSetName}/virtualMachines/{virtualmachineIndex}/networkInterfaces/{networkInterfaceName}/ipconfigurations/{ipConfigurationName}/publicipaddresses",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PublicIPAddressListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion1],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.networkInterfaceName,
    Parameters.virtualMachineScaleSetName,
    Parameters.virtualmachineIndex,
    Parameters.ipConfigurationName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getVirtualMachineScaleSetPublicIPAddressOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Compute/virtualMachineScaleSets/{virtualMachineScaleSetName}/virtualMachines/{virtualmachineIndex}/networkInterfaces/{networkInterfaceName}/ipconfigurations/{ipConfigurationName}/publicipaddresses/{publicIpAddressName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PublicIPAddress,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.expand, Parameters.apiVersion1],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.networkInterfaceName,
    Parameters.virtualMachineScaleSetName,
    Parameters.virtualmachineIndex,
    Parameters.ipConfigurationName,
    Parameters.publicIpAddressName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listCloudServicePublicIPAddressesNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PublicIPAddressListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.nextLink,
    Parameters.cloudServiceName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listCloudServiceRoleInstancePublicIPAddressesNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PublicIPAddressListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.nextLink,
    Parameters.cloudServiceName,
    Parameters.roleInstanceName,
    Parameters.networkInterfaceName,
    Parameters.ipConfigurationName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listAllNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PublicIPAddressListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  urlParameters: [Parameters.$host, Parameters.subscriptionId, Parameters.nextLink],
  headerParameters: [Parameters.accept],
  serializer,
};
const listNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PublicIPAddressListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.nextLink,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listVirtualMachineScaleSetPublicIPAddressesNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PublicIPAddressListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.nextLink,
    Parameters.virtualMachineScaleSetName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listVirtualMachineScaleSetVMPublicIPAddressesNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PublicIPAddressListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.nextLink,
    Parameters.networkInterfaceName,
    Parameters.virtualMachineScaleSetName,
    Parameters.virtualmachineIndex,
    Parameters.ipConfigurationName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
